import cn from "classnames"

import { Flex, Text, Tooltip } from "@/atoms"
import * as AdrDomain from "@/domain/adr"
import {
  DEFAULT_LONG_FORMAT,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import {
  EntityStatusIcon,
  InfoCircleOutlined,
  SafetyCustom,
  TagOutlined,
  WarningOutlined
} from "@/icons"
import { ContentDismissedTag } from "@/molecules/DismissedTag"
import { Heading } from "@/molecules/Heading"
import { PulsingDotLabel } from "@/molecules/PulsingDotLabel"
import { IncidentTag } from "./components/IncidentTag"

import styles from "./AdrSummary.module.scss"

import type { AdrSummaryProps } from "."

export const AdrSummary = (props: AdrSummaryProps) => {
  const { incident, palette = incident.priority } = props
  const { name, category, priority, createdAt, environment, dismissDetails } =
    incident
  const { namespace } = incident.where || {}
  const technicalId = AdrDomain.getTechnicalId(incident)
  const isCve = AdrDomain.isCVE(incident)
  const isMultipleCve = AdrDomain.isMultipleCVE(incident)
  const isSingleCve = isCve && !isMultipleCve
  const isAi = AdrDomain.getTechnicalType(incident) === "AI"
  const owaspPrefix = "OWASP "
  const owaspId = technicalId?.startsWith(owaspPrefix)
    ? technicalId.slice(owaspPrefix.length)
    : undefined
  const { reason, dismissedAt, dismissedBy } = dismissDetails || {}
  const isDismissed = typeof dismissedAt === "string"
  const isBlocked = AdrDomain.isBlocked(incident)

  const noValue = "unknown"
  const PriorityIcon =
    priority === "urgent" ? WarningOutlined : InfoCircleOutlined

  const { t } = useTranslation("translation", {
    keyPrefix: "triage.adr.summary"
  })

  return (
    <Flex vertical>
      <Flex
        className={cn(styles.meta, isBlocked && styles.metaBlocked)}
        align={isDismissed ? "flex-start" : "center"}
        justify="space-between"
        gap={32}
      >
        {!isDismissed && (
          <PulsingDotLabel
            text={
              <Heading
                level={6}
                title={isBlocked ? t("blocked_by_policy") : "Potential Exploit"}
              />
            }
          />
        )}

        {isDismissed && (
          <div className={styles.dismiss}>
            <EntityStatusIcon className={styles.dismissIcon} type="dismissed" />
            <ContentDismissedTag
              title="Incident"
              dismissedBy={dismissedBy || ""}
              reason={reason}
              timestamp={new Date(dismissedAt).getTime() / 1000}
              dismissMethod="manual"
            />
          </div>
        )}
        <Tooltip
          open={isCve ? undefined : false}
          rootClassName={styles.tooltipDetected}
          placement="bottomLeft"
          title={t("detection_tooltip_cve")}
        >
          <Flex align="center" gap={8}>
            {isCve && <InfoCircleOutlined className={styles.infoIcon} />}
            <Text className={cn(styles.textSm, isCve && styles.lineHeight1)}>
              {`Detected: ${getDateInUserLocale(createdAt, DEFAULT_LONG_FORMAT)}`}
            </Text>
          </Flex>
        </Tooltip>
      </Flex>
      <Flex
        vertical
        gap={12}
        className={cn(styles.wrapper, styles[`${palette}Theme`])}
      >
        <Heading level={2} title={name} classes={{ title: styles.title }} />
        <Flex gap={20}>
          <IncidentTag
            icon={<PriorityIcon />}
            text={
              <span className={cn("capitalize-text-ks", styles.fw500)}>
                {priority}
              </span>
            }
            palette={palette}
          />
          <Flex gap={8} align="center">
            <Text type="secondary" className={styles.label}>
              What
            </Text>
            <Flex gap={4}>
              {!isAi && <IncidentTag icon={<TagOutlined />} text={category} />}
              {(AdrDomain.isCWE(incident) || isSingleCve) && (
                <IncidentTag label="Technical ID" text={technicalId} />
              )}
              {AdrDomain.isMitre(incident) && (
                <IncidentTag label="MITRE Technique:" text={technicalId} />
              )}
              {isMultipleCve && (
                <IncidentTag
                  label="Multiple CVEs"
                  count={AdrDomain.getCountCve(incident)}
                />
              )}
              {isAi && (
                <Flex gap={4}>
                  <IncidentTag
                    icon={<SafetyCustom />}
                    text={t("ai_model_behavior")}
                  />
                  <IncidentTag label="MITRE ATLAS:" text={category} />
                  {owaspId && <IncidentTag label="OWASP:" text={owaspId} />}
                </Flex>
              )}
            </Flex>
          </Flex>
          <Flex gap={8} align="center">
            <Text type="secondary" className={styles.label}>
              Where
            </Text>
            <Flex gap={4}>
              <IncidentTag label="Environment:" text={environment || noValue} />
              {!AdrDomain.isVM(incident) && (
                <IncidentTag label="Namespace:" text={namespace || noValue} />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}
